'use client';
import React, { useEffect, useRef } from 'react';

interface PartnerCard {
  city: string;
  status: string;
  statusDotColor: string;
  contact: string;
  description: string;
  footer: string;
  featured?: boolean;
}

const partners: PartnerCard[] = [
  {
    city: 'Dallas',
    status: 'YEAR 1 · ENDORSED',
    statusDotColor: '#87926B',
    contact: 'Parks & Recreation and Development',
    description:
      'Endorsed the seven-park Dallas core survey covering the majority of the Year 1 dataset across Churchill, Old Renner, Frankford, Bentwood, Cheyenne, Preston Green, and Bent Tree Meadow.',
    footer: '7 parks · ~619 observations',
  },
  {
    city: 'Richardson',
    status: 'YEAR 1 · ENDORSED',
    statusDotColor: '#87926B',
    contact: 'Shohn Rodgers, Parks Department',
    description:
      'Endorsed the Cottonwood Park deployment. The riparian-corridor data from this survey will anchor Year 2\u2019s creek-ecosystem analysis.',
    footer: 'Cottonwood Park · 71 observations',
  },
  {
    city: 'University Park',
    status: 'YEAR 1 · ENDORSED',
    statusDotColor: '#87926B',
    contact: 'Parks Department',
    description:
      'Endorsed the Curtis Park deployment. The most ornamentally landscaped site in the inventory, providing a critical baseline for cultivated-versus-naturalized species comparison.',
    footer: 'Curtis Park · 63 observations',
  },
  {
    city: 'Azle',
    status: 'YEAR 1 · ENDORSED',
    statusDotColor: '#87926B',
    contact: 'Parks Department',
    description:
      'Engaged through the Shady Grove Park deployment on Eagle Mountain Lake — a lake-cove and marshland ecosystem unique within the Year 1 inventory.',
    footer: 'Shady Grove Park · 33 observations',
  },
  {
    city: 'Irving',
    status: 'YEAR 1 · ENDORSED · ACTIVE PARTNERSHIP',
    statusDotColor: '#B5582E',
    contact: 'Director Joe Moses, Irving Parks Department',
    description:
      'Director Moses personally endorsed the project and invited the NTBI research executive to extend the inventory to Irving. Field deployment at Lively Pointe Park added the fifth city to the Year 1 footprint and opens a pathway for collaboration with municipal arborists in Year 2.',
    footer: 'Lively Pointe Park · ~67 observations',
    featured: true,
  },
];

const PartnersSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll<HTMLElement>('.partner-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.style.opacity = '1';
                card.style.transform = 'translateY(0)';
              }, i * 120);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const regularPartners = partners.filter((p) => !p.featured);
  const featuredPartner = partners.find((p) => p.featured);

  return (
    <section
      id="partners"
      ref={sectionRef}
      className="py-20 md:py-28 px-6"
      style={{ background: 'var(--cream)', scrollMarginTop: '96px' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Eyebrow */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-canopy/8 border border-canopy/20 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-canopy" />
          <span className="text-canopy text-[10px] font-bold uppercase tracking-[0.35em]">
            Municipal Partnerships
          </span>
        </div>

        {/* Two-column: headline + intro */}
        <div className="grid md:grid-cols-2 gap-8 mb-14 items-start">
          <div>
            <h2 className="section-headline">
              The Year 1 inventory unfolded in partnership with{' '}
              <em
                style={{
                  fontStyle: 'italic',
                  fontWeight: 'inherit',
                  color: 'var(--sage)',
                }}
              >
                five North Texas municipal governments.
              </em>
            </h2>
          </div>
          <p className="text-umber/70 text-base md:text-lg leading-relaxed font-light max-w-md md:pt-2">
            Each park surveyed was endorsed by its city&apos;s parks department. Two municipal
            directors — Shohn Rodgers in Richardson, and Joe Moses in Irving — have personally
            engaged with the project and are helping shape Year 2 expansion.
          </p>
        </div>

        {/* Partner Cards Grid — 4 cards top row, Irving full-width below */}
        <div className="space-y-5">
          {/* Top row: 4 cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {regularPartners.map((partner) => (
              <div
                key={partner.city}
                className="partner-card"
                style={{
                  background: 'var(--cream)',
                  border: '1px solid rgba(16, 46, 29, 0.12)',
                  borderRadius: '8px',
                  padding: '36px 32px',
                  opacity: 1,
                  transform: 'none',
                  transition:
                    'opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1), transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0',
                }}
              >
                {/* Status row */}
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: partner.statusDotColor }}
                  />
                  <span
                    className="font-mono uppercase text-ash"
                    style={{ fontSize: '11px', letterSpacing: '0.08em' }}
                  >
                    {partner.status}
                  </span>
                </div>

                {/* City name */}
                <h3
                  className="font-display font-medium text-ink leading-tight mb-1"
                  style={{ fontSize: 'clamp(1.6rem, 2.5vw, 2rem)', fontVariationSettings: "'opsz' 144" }}
                >
                  {partner.city}
                </h3>

                {/* Contact */}
                <p
                  className="font-display text-ash mb-3"
                  style={{ fontSize: '15px', fontStyle: 'italic', lineHeight: 1.4 }}
                >
                  {partner.contact}
                </p>

                {/* Description */}
                <p
                  className="font-display text-ink/70 flex-1"
                  style={{ fontSize: '15px', lineHeight: 1.55 }}
                >
                  {partner.description}
                </p>

                {/* Footer */}
                <p
                  className="font-mono text-ash/70 mt-4 pt-4"
                  style={{
                    fontSize: '11px',
                    letterSpacing: '0.06em',
                    borderTop: '1px solid rgba(16, 46, 29, 0.10)',
                  }}
                >
                  {partner.footer}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom row: Irving — full width, featured */}
          {featuredPartner && (
            <div
              className="partner-card"
              style={{
                background: 'var(--cream)',
                border: '1.5px solid rgba(181, 88, 46, 0.30)',
                borderRadius: '8px',
                padding: '36px 32px',
                opacity: 1,
                transform: 'none',
                transition:
                  'opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1), transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              <div className="grid md:grid-cols-2 gap-8 items-start">
                {/* Left: status + city + contact */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: featuredPartner.statusDotColor }}
                    />
                    <span
                      className="font-mono uppercase text-sienna"
                      style={{ fontSize: '11px', letterSpacing: '0.08em' }}
                    >
                      {featuredPartner.status}
                    </span>
                  </div>
                  <h3
                    className="font-display font-medium text-ink leading-tight mb-1"
                    style={{
                      fontSize: 'clamp(2rem, 3.5vw, 2.5rem)',
                      fontVariationSettings: "'opsz' 144",
                    }}
                  >
                    {featuredPartner.city}
                  </h3>
                  <p
                    className="font-display text-ash"
                    style={{ fontSize: '15px', fontStyle: 'italic', lineHeight: 1.4 }}
                  >
                    {featuredPartner.contact}
                  </p>
                </div>

                {/* Right: description + footer */}
                <div className="flex flex-col justify-between h-full">
                  <p
                    className="font-display text-ink/70"
                    style={{ fontSize: '15px', lineHeight: 1.55 }}
                  >
                    {featuredPartner.description}
                  </p>
                  <p
                    className="font-mono text-ash/70 mt-4 pt-4"
                    style={{
                      fontSize: '11px',
                      letterSpacing: '0.06em',
                      borderTop: '1px solid rgba(16, 46, 29, 0.10)',
                    }}
                  >
                    {featuredPartner.footer}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
